import { useReducer } from "react";
import * as junipService from "@services/junip";
import { logError } from "@lib/logger";
import { NEWEST, RATING_ASC, RATING_DESC, REVIEWS_PER_PAGE } from "../constants";

export const REVIEWS_ACTIONS = {
  LOADING: "@reviews/LOADING",
  RESET: "@review/RESET",
  FETCH_FROM_JUNIP: "@reviews/FETCH_FROM_JUNIP",
  DISPLAY_MORE_REVIEWS: "@reviews/DISPLAY_MORE_REVIEWS",
  SORT_REVIEWS: "@review/SORT_REVIEWS",
};

const initialReviewsState = {
  junipProduct: null,
  junipReviews: [],
  page: { after: null, before: null },
  sortBy: NEWEST,
  isLoading: true,
};

function reviewsReducer(state, action) {
  switch (action.type) {
    case REVIEWS_ACTIONS.LOADING:
      return { ...state, isLoading: true };
    case REVIEWS_ACTIONS.RESET:
      return { ...state, page: { after: null, before: null }, sortBy: NEWEST, isLoading: false };
    case REVIEWS_ACTIONS.FETCH_FROM_JUNIP:
      return { ...state, ...action.payload, isLoading: false };
    case REVIEWS_ACTIONS.DISPLAY_MORE_REVIEWS:
      const updatedReviews = state.junipReviews.concat(action.payload.moreReviews);
      return {
        ...state,
        junipReviews: updatedReviews,
        page: action.payload.page,
        isLoading: false,
      };
    case REVIEWS_ACTIONS.SORT_REVIEWS:
      return {
        ...state,
        ...action.payload,
        isLoading: false,
      };
    default:
      return state;
  }
}

function getSortParams(value) {
  let sortField = "created_at";
  let sortOrder = "desc";
  if (value === RATING_ASC) {
    sortField = "rating";
    sortOrder = "asc";
  } else if (value === RATING_DESC) {
    sortField = "rating";
    sortOrder = "desc";
  }

  return { sortField, sortOrder };
}

export async function getInitialReviews(dispatch, shopifyProductID) {
  try {
    dispatch({ type: REVIEWS_ACTIONS.LOADING });

    const junipProduct = await junipService.junipProduct(shopifyProductID);
    if (junipProduct) {
      const junipReviews = await junipService.junipReviews(junipProduct.id, null, REVIEWS_PER_PAGE);
      dispatch({
        type: REVIEWS_ACTIONS.FETCH_FROM_JUNIP,
        payload: {
          junipProduct,
          junipReviews: junipReviews.product_reviews,
          page: junipReviews.meta.page,
        },
      });
    } else {
      dispatch({ type: REVIEWS_ACTIONS.RESET });
    }
    return;
  } catch (error) {
    logError(error);
  }
}

export async function fetchMoreReviews(dispatch, id, after, sortLabel) {
  try {
    dispatch({ type: REVIEWS_ACTIONS.LOADING });
    const { sortField, sortOrder } = getSortParams(sortLabel);
    const moreReviews = await junipService.junipReviews(
      id,
      after,
      REVIEWS_PER_PAGE,
      sortField,
      sortOrder
    );
    dispatch({
      type: REVIEWS_ACTIONS.DISPLAY_MORE_REVIEWS,
      payload: {
        moreReviews: moreReviews.product_reviews,
        page: moreReviews.meta.page,
      },
    });
    return;
  } catch (error) {
    logError(error);
  }
}

export async function sortReviews(dispatch, id, sortLabel) {
  try {
    dispatch({ type: REVIEWS_ACTIONS.LOADING });
    const { sortField, sortOrder } = getSortParams(sortLabel);
    const junipReviews = await junipService.junipReviews(
      id,
      null,
      REVIEWS_PER_PAGE,
      sortField,
      sortOrder
    );
    dispatch({
      type: REVIEWS_ACTIONS.SORT_REVIEWS,
      payload: {
        junipReviews: junipReviews.product_reviews,
        sortBy: sortLabel,
        page: junipReviews.meta.page,
      },
    });
    return;
  } catch (error) {
    logError(error);
  }
}

export function useReviews(initialJunipProduct, initialJunipReviews) {
  const [state, dispatch] = useReducer(reviewsReducer, {
    ...initialReviewsState,
    junipProduct: initialJunipProduct,
    junipReviews: initialJunipReviews.product_reviews ? initialJunipReviews.product_reviews : [],
  });

  return { state, dispatch };
}
